// meetingRooms/components/prices/TariffCard.js - Fixed to prevent reset after save
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  CardContent
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import PriceField from './PriceField';

// Import honey-themed styled components
import {
  StyledTariffCard,
  StyledConfigSection,
  StyledFormRow,
  StyledFormLabel,
  StyledSaveButton
} from './styled';

// Constants
const SLOT_DURATIONS = [
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: 150, label: '2.5 hours' },
  { value: 180, label: '3 hours' },
  { value: 210, label: '3.5 hours' },
  { value: 240, label: '4 hours' },
  { value: 270, label: '4.5 hours' },
  { value: 300, label: '5 hours' },
  { value: 330, label: '5.5 hours' },
  { value: 360, label: '6 hours' },
  { value: 390, label: '6.5 hours' },
  { value: 420, label: '7 hours' },
  { value: 450, label: '7.5 hours' },
  { value: 480, label: '8 hours' }
];

const TariffCard = ({ tariff, isVatInclusive, vatRate, onTariffUpdated, resource, showSnackbar }) => {
  const [editedTariff, setEditedTariff] = useState({
    ...tariff,
    slot_length: tariff.slot_length || 60,
    slot_price: tariff.slot_price || '0.00',
    minimum_booking_length: tariff.minimum_booking_length || 60,
    enable_half_day: !!tariff.enable_half_day,
    half_day_price: tariff.half_day_price || '0.00',
    enable_daily: !!tariff.enable_daily,
    daily_price: tariff.daily_price || '0.00'
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // Track if we just saved to prevent reset
  const justSavedRef = useRef(false);
  const lastTariffRef = useRef(tariff);

  // Only update state when tariff prop changes AND we didn't just save
  useEffect(() => {
    // Skip update if we just saved (prevents reset)
    if (justSavedRef.current) {
      justSavedRef.current = false;
      lastTariffRef.current = tariff;
      return;
    }

    // Only update if the tariff actually changed (not just a re-render)
    const tariffChanged = JSON.stringify(lastTariffRef.current) !== JSON.stringify(tariff);
    
    if (tariffChanged) {
      setEditedTariff({
        ...tariff,
        slot_length: tariff.slot_length || 60,
        slot_price: tariff.slot_price || '0.00',
        minimum_booking_length: tariff.minimum_booking_length || 60,
        enable_half_day: !!tariff.enable_half_day,
        half_day_price: tariff.half_day_price || '0.00',
        enable_daily: !!tariff.enable_daily,
        daily_price: tariff.daily_price || '0.00'
      });
      lastTariffRef.current = tariff;
    }
  }, [tariff]);
  
  // Calculate minimum slots
  const calculateMinSlots = () => {
    if (editedTariff.minimum_booking_length && editedTariff.slot_length) {
      return Math.ceil(editedTariff.minimum_booking_length / editedTariff.slot_length);
    }
    return 1;
  };
  
  // Handle form field changes
  const handleChange = (field, value) => {
    setEditedTariff(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle date field changes
  const handleDateChange = (field, value) => {
    const formattedValue = field === 'start' 
      ? `${value}T00:00:00` 
      : `${value}T23:59:59`;
      
    setEditedTariff(prev => ({
      ...prev,
      [field]: formattedValue
    }));
  };

  // Handle toggle for boolean fields
  const handleToggle = (field) => {
    setEditedTariff(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  
  // When min_slots changes, update minimum_booking_length
  const handleMinSlotsChange = (value) => {
    const minimum_booking_length = value * editedTariff.slot_length;
    setEditedTariff(prev => ({
      ...prev,
      minimum_booking_length
    }));
  };

  // FIXED save function - prevents reset after save
  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      const tariffData = {
        name: editedTariff.name,
        status: editedTariff.status,
        start: editedTariff.start,
        end: editedTariff.end,
        booking_type: "MR",
        resource: resource?.slug || editedTariff.resource,
        slot_length: editedTariff.slot_length,
        slot_price: editedTariff.slot_price || '0.00',
        minimum_booking_length: editedTariff.minimum_booking_length,
        enable_half_day: !!editedTariff.enable_half_day,
        half_day_price: editedTariff.half_day_price || '0.00',
        enable_daily: !!editedTariff.enable_daily,
        daily_price: editedTariff.daily_price || '0.00'
      };
      
      // Set flag to prevent reset after save
      justSavedRef.current = true;
      
      // Call parent's update function with tariff slug and data
      if (onTariffUpdated) {
        const result = await onTariffUpdated(editedTariff.slug, tariffData);
        
        // Update editedTariff with the returned data to stay in sync
        if (result) {
          setEditedTariff(prev => ({
            ...prev,
            ...result
          }));
        }
      }
    } catch (error) {
      console.error('❌ Error saving tariff:', error);
      // Reset the flag on error
      justSavedRef.current = false;
      
      if (showSnackbar) {
        showSnackbar('Failed to save tariff changes', 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <StyledTariffCard>
      <CardContent sx={{ pt: 3 }}>
        {/* Validity period and status in 3 columns */}
        <StyledConfigSection>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Valid from"
                type="date"
                value={editedTariff.start ? editedTariff.start.split('T')[0] : ''}
                onChange={(e) => handleDateChange('start', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Valid to"
                type="date"
                value={editedTariff.end ? editedTariff.end.split('T')[0] : ''}
                onChange={(e) => handleDateChange('end', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Status"
                value={editedTariff.status || 'AC'}
                onChange={(e) => handleChange('status', e.target.value)}
                size="small"
                fullWidth
                SelectProps={{
                  native: true,
                }}
              >
                <option value="AC">Active</option>
                <option value="IN">Inactive</option>
              </TextField>
            </Grid>
          </Grid>
          
          {/* Small VAT toggle in bottom right */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-end',
            mt: 1
          }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={isVatInclusive} 
                  onChange={(e) => {}} // Make read-only for now
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#F1C40F',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#D4A574',
                    },
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: '#8B7355', fontSize: '0.75rem' }}>
                  {isVatInclusive ? 'Show here the price without VAT' : 'Show here the price with VAT'}
                </Typography>
              }
              sx={{ 
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.75rem'
                }
              }}
            />
          </Box>
        </StyledConfigSection>

        {/* Slot Configuration */}
        <StyledConfigSection>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, color: '#5D4E37' }}>
            Slot Configuration
            <Tooltip title="Define how the meeting room can be booked in time slots">
              <InfoIcon fontSize="small" sx={{ ml: 1, opacity: 0.6 }} />
            </Tooltip>
          </Typography>

          <StyledFormRow>
            <StyledFormLabel>Slot Length:</StyledFormLabel>
            <FormControl fullWidth size="small">
              <Select
                value={editedTariff.slot_length || 60}
                onChange={(e) => handleChange('slot_length', e.target.value)}
              >
                {SLOT_DURATIONS.map((slot) => (
                  <MenuItem key={slot.value} value={slot.value}>
                    {slot.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </StyledFormRow>

          <StyledFormRow>
            <StyledFormLabel>Slot Price:</StyledFormLabel>
            <PriceField 
              value={editedTariff.slot_price || '0.00'}
              onChange={(value) => handleChange('slot_price', value)}
              isVatInclusive={isVatInclusive}
              vatRate={vatRate}
              fullWidth
            />
          </StyledFormRow>

          <StyledFormRow>
            <StyledFormLabel>Minimum slots:</StyledFormLabel>
            <FormControl fullWidth size="small">
              <Select
                value={calculateMinSlots()}
                onChange={(e) => handleMinSlotsChange(e.target.value)}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                  <MenuItem key={num} value={num}>
                    {num} {num === 1 ? 'slot' : 'slots'} ({num * (editedTariff.slot_length || 60)/60} {num * (editedTariff.slot_length || 60)/60 === 1 ? 'hour' : 'hours'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </StyledFormRow>
        </StyledConfigSection>

        {/* Half-day and Full-day Options */}
        <StyledConfigSection>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, color: '#5D4E37' }}>
            Half & Full Day Options
            <Tooltip title="Enable half-day and full-day booking options with special pricing">
              <InfoIcon fontSize="small" sx={{ ml: 1, opacity: 0.6 }} />
            </Tooltip>
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editedTariff.enable_half_day}
                    onChange={() => handleToggle('enable_half_day')}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#F1C40F',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#D4A574',
                      },
                    }}
                  />
                }
                label="Allow Half-day booking"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PriceField 
                label="Half-day Price"
                value={editedTariff.half_day_price || '0.00'}
                onChange={(value) => handleChange('half_day_price', value)}
                isVatInclusive={isVatInclusive}
                vatRate={vatRate}
                disabled={!editedTariff.enable_half_day}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editedTariff.enable_daily}
                    onChange={() => handleToggle('enable_daily')}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#F1C40F',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#D4A574',
                      },
                    }}
                  />
                }
                label="Allow Full-day booking"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PriceField 
                label="Full-day Price"
                value={editedTariff.daily_price || '0.00'}
                onChange={(value) => handleChange('daily_price', value)}
                isVatInclusive={isVatInclusive}
                vatRate={vatRate}
                disabled={!editedTariff.enable_daily}
              />
            </Grid>
          </Grid>
        </StyledConfigSection>

        {/* Save button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <StyledSaveButton
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
            size="medium"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </StyledSaveButton>
        </Box>
      </CardContent>
    </StyledTariffCard>
  );
};

export default TariffCard;